import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { api, fmtEUR, STATUT_LABELS, STATUT_COLORS } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Save, RotateCcw, FlaskConical, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import SortHeader, { useSort, sortArr } from '../components/SortHeader';

const MODE_LABELS = {
  aucun: 'Aucune',
  pct_ca: '% du CA',
  pct_ca_apres_assurance: '% (CA − Assurance)',
  manuel: 'Manuel',
};

// Calcul live de la rémunération à partir des champs
function calcRemu(p) {
  const honoraires = p.honoraires_bruts || 0;
  const assurance = p.assurance || 0;
  const m = p.remu_dirig_mode || 'aucun';
  if (m === 'aucun') return 0;
  if (m === 'manuel') return Number(p.remu_dirig_montant_manuel) || 0;
  const taux = Number(p.remu_dirig_taux) || 0;
  if (m === 'pct_ca') return honoraires * taux;
  if (m === 'pct_ca_apres_assurance') return Math.max(0, (honoraires - assurance) * taux);
  return 0;
}

export default function RemunerationDirigeants() {
  const [rent, setRent] = useState([]);
  const [overrides, setOverrides] = useState({}); // {projet_id: {remu_dirig_mode, remu_dirig_taux, remu_dirig_montant_manuel}}
  const [filterAnnee, setFilterAnnee] = useState('all');
  const [filterStatut, setFilterStatut] = useState('all');
  const [simulation, setSimulation] = useState(true); // Mode simulation par défaut (modifications non persistées)
  const [sort, toggleSort] = useSort('remu_simule', 'desc');

  const load = async () => {
    const { data } = await api.get('/dashboard/rentabilite-projets');
    setRent(data);
    setOverrides({});
  };
  useEffect(() => { load(); }, []);

  const anneesDispo = useMemo(() => {
    const set = new Set();
    for (const p of rent) {
      const ar = p.annee_realisation;
      if (ar) set.add(ar);
    }
    return Array.from(set).sort((a, b) => b - a);
  }, [rent]);

  const filtered = useMemo(() =>
    rent.filter(p => {
      if (filterAnnee !== 'all' && String(p.annee_realisation || '') !== String(filterAnnee)) return false;
      if (filterStatut !== 'all' && p.statut !== filterStatut) return false;
      return true;
    }),
  [rent, filterAnnee, filterStatut]);

  // Applique les overrides aux projets pour le calcul live
  const projects = useMemo(() => filtered.map(p => {
    const ov = overrides[p.id] || {};
    const merged = {
      ...p,
      remu_dirig_mode: ov.remu_dirig_mode ?? p.remu_dirig_mode,
      remu_dirig_taux: ov.remu_dirig_taux ?? p.remu_dirig_taux,
      remu_dirig_montant_manuel: ov.remu_dirig_montant_manuel ?? p.remu_dirig_montant_manuel,
    };
    const remu_simule = calcRemu(merged);
    const marge_simule = (p.honoraires_bruts || 0) - (p.cout_salaries || 0) - (p.assurance || 0) - remu_simule;
    return { ...merged, remu_simule, marge_simule, has_override: !!overrides[p.id] };
  }), [filtered, overrides]);

  const sorted = useMemo(() => sortArr(projects, sort, (p, k) => {
    if (k === 'statut') return STATUT_LABELS[p.statut] || '';
    return p[k];
  }), [projects, sort]);

  // KPIs
  const kpis = useMemo(() => {
    const totalPrevu = projects.reduce((s, p) => s + (p.remu_dirig || 0), 0);
    const totalSimule = projects.reduce((s, p) => s + p.remu_simule, 0);
    const totalHonoraires = projects.reduce((s, p) => s + (p.honoraires_bruts || 0), 0);
    const totalMargeSimule = projects.reduce((s, p) => s + p.marge_simule, 0);
    const nbAvecRemu = projects.filter(p => p.remu_simule > 0).length;
    return { totalPrevu, totalSimule, totalHonoraires, totalMargeSimule, nbAvecRemu, ecart: totalSimule - totalPrevu };
  }, [projects]);

  const setOverride = (id, field, value) => {
    setOverrides(o => ({
      ...o,
      [id]: { ...(o[id] || {}), [field]: value },
    }));
  };

  const resetAll = () => {
    if (Object.keys(overrides).length === 0) return;
    if (!window.confirm('Réinitialiser toutes les modifications de simulation ?')) return;
    setOverrides({});
  };

  const saveAll = async () => {
    const ids = Object.keys(overrides);
    if (ids.length === 0) { toast.info('Aucune modification à sauvegarder'); return; }
    if (!window.confirm(`Appliquer les ${ids.length} modification(s) sur les projets concernés ?`)) return;
    try {
      for (const id of ids) {
        const p = rent.find(x => x.id === id);
        if (!p) continue;
        const ov = overrides[id];
        await api.put(`/projets/${id}`, {
          nom: p.nom,
          remu_dirig_mode: ov.remu_dirig_mode ?? p.remu_dirig_mode,
          remu_dirig_taux: ov.remu_dirig_taux ?? p.remu_dirig_taux,
          remu_dirig_montant_manuel: ov.remu_dirig_montant_manuel ?? p.remu_dirig_montant_manuel,
        });
      }
      toast.success(`${ids.length} projet(s) mis à jour`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur sauvegarde');
    }
  };

  const hasModifs = Object.keys(overrides).length > 0;

  return (
    <Layout
      title="Rémunération dirigeants"
      subtitle="Simulations & projections — Meriem & Yassine"
      actions={
        <div className="flex items-center gap-2">
          <button onClick={() => setSimulation(s => !s)}
            className={`px-3 py-2 border text-xs uppercase tracking-wider inline-flex items-center gap-1.5 ${simulation ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-violet-700 border-violet-300 hover:bg-violet-50'}`}
            data-testid="toggle-simulation" title="Mode simulation : les modifs ne sont pas sauvegardées">
            <FlaskConical size={13} /> {simulation ? 'Simulation' : 'Édition directe'}
          </button>
          {hasModifs && (
            <>
              <button onClick={resetAll} className="px-3 py-2 border border-zinc-300 text-zinc-700 text-xs uppercase tracking-wider inline-flex items-center gap-1.5 hover:bg-zinc-100" data-testid="reset-all">
                <RotateCcw size={13} /> Annuler ({Object.keys(overrides).length})
              </button>
              <Button onClick={saveAll} className="rounded-none bg-[#002FA7] hover:bg-[#001B63]" data-testid="save-all">
                <Save size={14} className="mr-1.5" /> Appliquer aux projets
              </Button>
            </>
          )}
        </div>
      }
    >
      {/* KPI bandeau */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-zinc-200 border-l-4 border-l-violet-500 p-5" data-testid="kpi-prevu">
          <div className="text-[10px] uppercase tracking-[0.2em] text-violet-700 mb-1">Prévu (sauvegardé)</div>
          <div className="font-mono font-medium text-2xl">{fmtEUR(kpis.totalPrevu)}</div>
          <div className="text-[10px] text-zinc-500 mt-1">{kpis.nbAvecRemu} projet(s) avec rémunération</div>
        </div>
        <div className="bg-white border border-zinc-200 border-l-4 border-l-violet-700 p-5" data-testid="kpi-simule">
          <div className="text-[10px] uppercase tracking-[0.2em] text-violet-800 mb-1">Simulé (en cours)</div>
          <div className="font-mono font-medium text-2xl">{fmtEUR(kpis.totalSimule)}</div>
          <div className={`text-[10px] mt-1 ${kpis.ecart === 0 ? 'text-zinc-500' : kpis.ecart > 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
            Écart {kpis.ecart >= 0 ? '+' : ''}{fmtEUR(kpis.ecart)}
          </div>
        </div>
        <div className="bg-white border border-zinc-200 p-5" data-testid="kpi-pct-honoraires">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">% des honoraires</div>
          <div className="font-mono font-medium text-2xl">
            {kpis.totalHonoraires > 0 ? `${(kpis.totalSimule / kpis.totalHonoraires * 100).toFixed(1)}%` : '—'}
          </div>
          <div className="text-[10px] text-zinc-500 mt-1">CA total : {fmtEUR(kpis.totalHonoraires)}</div>
        </div>
        <div className={`bg-white border border-zinc-200 border-l-4 ${kpis.totalMargeSimule >= 0 ? 'border-l-emerald-500' : 'border-l-rose-500'} p-5`} data-testid="kpi-marge-simule">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Marge agence (après rémun.)</div>
          <div className={`font-mono font-medium text-2xl ${kpis.totalMargeSimule >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>{fmtEUR(kpis.totalMargeSimule)}</div>
          <div className="text-[10px] text-zinc-500 mt-1">Honoraires − coûts − assurance − rémun.</div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white border border-zinc-200 p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 mb-1">Année réalisation</div>
          <Select value={filterAnnee} onValueChange={setFilterAnnee}>
            <SelectTrigger className="rounded-none w-32" data-testid="filter-annee"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">Toutes</SelectItem>
              {anneesDispo.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 mb-1">Statut</div>
          <Select value={filterStatut} onValueChange={setFilterStatut}>
            <SelectTrigger className="rounded-none w-56" data-testid="filter-statut"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">Tous</SelectItem>
              {Object.entries(STATUT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto text-[11px] text-zinc-500 italic max-w-md text-right">
          {simulation
            ? 'Mode simulation : modifiez librement, les changements ne sont pas appliqués aux projets tant que vous ne cliquez pas sur « Appliquer aux projets ».'
            : 'Mode édition directe : utilisez « Appliquer » pour sauvegarder.'}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 max-h-[calc(100vh-380px)] overflow-auto" data-testid="remu-table">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-white border-b-2 border-zinc-900">
            <tr className="text-left">
              <SortHeader sort={sort} onToggle={toggleSort} k="code" label="Code" />
              <SortHeader sort={sort} onToggle={toggleSort} k="nom" label="Nom" />
              <SortHeader sort={sort} onToggle={toggleSort} k="statut" label="Statut" />
              <SortHeader sort={sort} onToggle={toggleSort} k="honoraires_bruts" label="Honoraires" align="right" />
              <SortHeader sort={sort} onToggle={toggleSort} k="assurance" label="Assurance" align="right" />
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium min-w-[150px]">Mode</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right w-[80px]">Taux / €</th>
              <SortHeader sort={sort} onToggle={toggleSort} k="remu_simule" label="Rémun." align="right" />
              <SortHeader sort={sort} onToggle={toggleSort} k="marge_simule" label="Marge" align="right" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => {
              const margeColor = p.marge_simule >= 0 ? 'text-emerald-700' : 'text-rose-600';
              const ov = overrides[p.id];
              const taux = ov?.remu_dirig_taux ?? p.remu_dirig_taux ?? 0;
              const manuel = ov?.remu_dirig_montant_manuel ?? p.remu_dirig_montant_manuel ?? 0;
              const mode = ov?.remu_dirig_mode ?? p.remu_dirig_mode ?? 'aucun';
              return (
                <tr key={p.id} className={`border-b border-zinc-100 ${p.has_override ? 'bg-violet-50/40' : 'hover:bg-zinc-50'}`} data-testid={`row-${p.code}`}>
                  <td className="px-3 py-2 font-mono text-xs font-medium">{p.code}</td>
                  <td className="px-3 py-2 text-xs">{p.nom}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-medium border ${STATUT_COLORS[p.statut]}`}>{STATUT_LABELS[p.statut]}</span>
                  </td>
                  <td className="px-3 py-2 font-mono text-right text-xs text-[#002FA7]">{fmtEUR(p.honoraires_bruts || 0)}</td>
                  <td className="px-3 py-2 font-mono text-right text-xs text-amber-700">{fmtEUR(p.assurance || 0)}</td>
                  <td className="px-2 py-1.5">
                    <Select value={mode} onValueChange={(v) => setOverride(p.id, 'remu_dirig_mode', v)}>
                      <SelectTrigger className="rounded-none h-8 text-xs" data-testid={`mode-${p.code}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        {Object.entries(MODE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-2 py-1.5">
                    {(mode === 'pct_ca' || mode === 'pct_ca_apres_assurance') && (
                      <Input type="number" step="0.5" className="rounded-none h-8 text-xs font-mono text-right [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                        value={(taux * 100).toFixed(1)}
                        onChange={(e) => setOverride(p.id, 'remu_dirig_taux', (Number(e.target.value) || 0) / 100)}
                        data-testid={`taux-${p.code}`} />
                    )}
                    {mode === 'manuel' && (
                      <Input type="number" className="rounded-none h-8 text-xs font-mono text-right [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                        value={manuel}
                        onChange={(e) => setOverride(p.id, 'remu_dirig_montant_manuel', Number(e.target.value) || 0)}
                        data-testid={`manuel-${p.code}`} />
                    )}
                    {mode === 'aucun' && <span className="text-[11px] text-zinc-300">—</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-right font-medium text-violet-700">{fmtEUR(p.remu_simule)}</td>
                  <td className={`px-3 py-2 font-mono text-right font-medium ${margeColor}`}>{fmtEUR(p.marge_simule)}</td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-zinc-400 text-sm">Aucun projet ne correspond aux filtres.</td></tr>
            )}
          </tbody>
          <tfoot className="bg-zinc-50 border-t-2 border-zinc-900 font-semibold sticky bottom-0">
            <tr>
              <td colSpan={3} className="px-3 py-3 text-[11px] uppercase tracking-[0.15em]">Total ({sorted.length})</td>
              <td className="px-3 py-3 font-mono text-right text-xs text-[#002FA7]">{fmtEUR(kpis.totalHonoraires)}</td>
              <td className="px-3 py-3 font-mono text-right text-xs text-amber-700">{fmtEUR(sorted.reduce((s, p) => s + (p.assurance || 0), 0))}</td>
              <td colSpan={2}></td>
              <td className="px-3 py-3 font-mono text-right text-violet-700" data-testid="footer-remu-total">{fmtEUR(kpis.totalSimule)}</td>
              <td className={`px-3 py-3 font-mono text-right ${kpis.totalMargeSimule >= 0 ? 'text-emerald-700' : 'text-rose-600'}`} data-testid="footer-marge-total">{fmtEUR(kpis.totalMargeSimule)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="mt-4 px-4 py-3 bg-violet-50/40 border border-violet-200 text-xs text-violet-900 flex items-start gap-2">
        <TrendingUp size={14} className="mt-0.5 shrink-0" />
        <div>
          <strong>Comment utiliser cette page :</strong> modifiez librement les modes (% CA / % CA-Assurance / Manuel) et les taux pour voir l&apos;impact en temps réel sur la rémunération totale et la marge agence. Les lignes modifiées passent en surbrillance violette. Cliquez sur <strong>« Appliquer aux projets »</strong> pour sauvegarder, ou <strong>« Annuler »</strong> pour revenir aux valeurs initiales.
        </div>
      </div>
    </Layout>
  );
}
